import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideActivity,
  lucideCheckCircle2,
  lucideAlertTriangle,
  lucideClock,
  lucideInbox,
  lucideRefreshCw,
  lucideSearch,
  lucideExternalLink,
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';

import {
  IntegrationMonitorService,
  WebhookLog,
  PagedWebhookLogs,
  IntegrationHealth,
  TimelineEvent,
  ErrorPattern,
} from '../../../../services/integration-monitor.service';

import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmLabel } from '@spartan-ng/helm/label';
import { HlmInput } from '@spartan-ng/helm/input';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';

@Component({
  selector: 'app-integrations-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './integrations-tab.component.html',
  providers: [
    provideIcons({
      lucideActivity,
      lucideCheckCircle2,
      lucideAlertTriangle,
      lucideClock,
      lucideInbox,
      lucideRefreshCw,
      lucideSearch,
      lucideExternalLink,
    }),
  ],
  imports: [
    DatePipe,
    DecimalPipe,
    TitleCasePipe,
    FormsModule,
    NgIcon,
    HlmIcon,
    ...HlmCardImports,
    HlmBadge,
    HlmButton,
    ...HlmTableImports,
    ...HlmSkeletonImports,
    ...HlmDialogImports,
    ...BrnDialogImports,
    HlmLabel,
    HlmInput,
  ],
})
export class IntegrationsTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() autoRefresh = false;

  private readonly monitorService = inject(IntegrationMonitorService);
  private readonly destroyRef = inject(DestroyRef);

  // Health summary
  readonly healthSummary = signal<IntegrationHealth[]>([]);
  readonly healthSummaryLoading = signal(false);

  // Webhook logs
  readonly webhookLogs = signal<WebhookLog[]>([]);
  readonly totalCount = signal(0);
  readonly webhookLogsLoading = signal(false);
  readonly page = signal(1);
  readonly pageSize = 20;

  // Webhook detail dialog
  readonly selectedLog = signal<WebhookLog | null>(null);
  readonly logDetailOpen = signal(false);
  readonly logDetailLoading = signal(false);

  // Webhook log filters
  webhookFilter = {
    source: '',
    status: '',
    from: '',
    to: '',
  };

  // Order timeline
  readonly timelineOrderId = signal('');
  readonly timelineSearchInput = signal('');
  readonly timelineEvents = signal<TimelineEvent[]>([]);
  readonly timelineLoading = signal(false);
  readonly timelineError = signal('');

  // Error patterns
  readonly errorPatterns = signal<ErrorPattern[]>([]);
  readonly errorPatternsLoading = signal(false);

  // ---- Computed pagination ----
  readonly totalPages = computed(() => Math.ceil(this.totalCount() / this.pageSize));
  readonly pageEnd = computed(() => Math.min(this.page() * this.pageSize, this.totalCount()));
  readonly pageNumbers = computed(() =>
    Array.from({ length: Math.min(this.totalPages(), 7) }, (_, i) => i + 1)
  );

  // ---- Computed stat-tile metrics ----

  /** Total webhook calls across all integrations in the last 24 h */
  readonly statTotalWebhooks = computed(() =>
    this.healthSummary().reduce((sum, h) => sum + h.totalRequests24h, 0)
  );

  /**
   * Weighted-average success rate across all integrations.
   * Falls back to 0 when there are no requests yet.
   */
  readonly statSuccessRate = computed(() => {
    const integrations = this.healthSummary();
    const totalReqs = integrations.reduce((sum, h) => sum + h.totalRequests24h, 0);
    if (totalReqs === 0) return 0;
    const weightedSum = integrations.reduce(
      (sum, h) => sum + h.successRate24h * h.totalRequests24h,
      0
    );
    return Math.round((weightedSum / totalReqs) * 10) / 10;
  });

  /**
   * Estimated failed calls: totalRequests24h × (1 - successRate24h / 100).
   * Rounds to nearest integer.
   */
  readonly statFailedCalls = computed(() => {
    const integrations = this.healthSummary();
    return Math.round(
      integrations.reduce(
        (sum, h) => sum + h.totalRequests24h * (1 - h.successRate24h / 100),
        0
      )
    );
  });

  /**
   * Simple average of avgResponseTimeMs across all integrations.
   * Shows 0 when no data yet.
   */
  readonly statAvgResponseMs = computed(() => {
    const integrations = this.healthSummary();
    if (integrations.length === 0) return 0;
    const sum = integrations.reduce((acc, h) => acc + h.avgResponseTimeMs, 0);
    return Math.round(sum / integrations.length);
  });

  private refreshTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['autoRefresh']) {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = undefined;
      }
      if (this.autoRefresh) {
        this.refreshTimer = setInterval(() => this.loadAll(), 30000);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  private loadAll(): void {
    this.loadHealthSummary();
    this.loadWebhookLogs();
    this.loadErrorPatterns();
  }

  loadHealthSummary(): void {
    this.healthSummaryLoading.set(true);
    this.monitorService
      .getHealthSummary()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.healthSummary.set(data);
          this.healthSummaryLoading.set(false);
        },
        error: () => this.healthSummaryLoading.set(false),
      });
  }

  loadWebhookLogs(): void {
    this.webhookLogsLoading.set(true);
    const f = this.webhookFilter;

    this.monitorService
      .getWebhookLogs(
        this.page(),
        this.pageSize,
        f.source || undefined,
        f.status || undefined,
        f.from || undefined,
        f.to || undefined
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: PagedWebhookLogs) => {
          this.webhookLogs.set(data.items);
          this.totalCount.set(data.totalCount);
          this.webhookLogsLoading.set(false);
        },
        error: () => this.webhookLogsLoading.set(false),
      });
  }

  loadErrorPatterns(): void {
    this.errorPatternsLoading.set(true);
    this.monitorService
      .getErrorPatterns()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.errorPatterns.set(data);
          this.errorPatternsLoading.set(false);
        },
        error: () => this.errorPatternsLoading.set(false),
      });
  }

  applyWebhookFilters(): void {
    this.page.set(1);
    this.loadWebhookLogs();
  }

  resetWebhookFilters(): void {
    this.webhookFilter = { source: '', status: '', from: '', to: '' };
    this.page.set(1);
    this.loadWebhookLogs();
  }

  setPage(p: number): void {
    this.page.set(p);
    this.loadWebhookLogs();
  }

  openLogDetail(log: WebhookLog): void {
    if (log.requestBody !== undefined || log.responseBody !== undefined) {
      this.selectedLog.set(log);
      this.logDetailOpen.set(true);
      return;
    }
    this.logDetailLoading.set(true);
    this.selectedLog.set(log);
    this.logDetailOpen.set(true);

    this.monitorService
      .getWebhookLogDetail(log.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          this.selectedLog.set(detail);
          this.logDetailLoading.set(false);
        },
        error: () => this.logDetailLoading.set(false),
      });
  }

  closeLogDetail(): void {
    this.logDetailOpen.set(false);
    this.selectedLog.set(null);
  }

  searchTimeline(): void {
    const id = this.timelineSearchInput().trim();
    if (!id) return;

    this.timelineOrderId.set(id);
    this.timelineLoading.set(true);
    this.timelineError.set('');

    this.monitorService
      .getOrderTimeline(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: events => {
          this.timelineEvents.set(events);
          this.timelineLoading.set(false);
          if (events.length === 0) {
            this.timelineError.set('No events found for this order ID.');
          }
        },
        error: () => {
          this.timelineError.set('Failed to load timeline. Please check the order ID.');
          this.timelineLoading.set(false);
        },
      });
  }

  clearTimeline(): void {
    this.timelineOrderId.set('');
    this.timelineSearchInput.set('');
    this.timelineEvents.set([]);
    this.timelineError.set('');
  }

  getStatusBadgeVariant(statusCode: number): 'default' | 'secondary' | 'destructive' | 'outline' {
    if (statusCode >= 200 && statusCode < 300) return 'default';
    if (statusCode >= 400 && statusCode < 500) return 'secondary';
    if (statusCode >= 500) return 'destructive';
    return 'outline';
  }

  getHealthBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' {
    switch (status) {
      case 'healthy': return 'default';
      case 'degraded': return 'secondary';
      default: return 'destructive';
    }
  }

  getTimelineEventColor(eventType: string): string {
    const type = eventType.toLowerCase();
    if (type.includes('webhook') || type.includes('received')) return 'bg-blue-500';
    if (type.includes('created')) return 'bg-green-500';
    if (type.includes('updated')) return 'bg-amber-500';
    if (type.includes('scan')) return 'bg-purple-500';
    if (type.includes('error')) return 'bg-red-500';
    return 'bg-slate-400';
  }

  tryFormatJson(value: string | undefined): string {
    if (!value) return '';
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
}
